import { ScrollableLayout } from '@/components/Layout';
import Page from '@/components/Layout/Page';
import { Button } from '@/components/ui/elements/button';
import { PolicyGate, useAuthorization } from '@/features/auth/authorization';
import { B2bDashboard } from '@/features/b2b/routes/B2bDashboard';
import { CallCenterDashboard } from '@/features/callcenter/routes/CallCenterDashboard';
import { ShiftEventSection } from '@/features/shift-dashboard/components/ShiftEventSection';
import { useShiftData } from '@/features/shift-dashboard/hooks/useShiftData';
import { ShiftDashboard } from '@/features/shift-dashboard/routes/ShiftDashboard';
import { AuthContext } from '@/providers/auth';
import { LocationContext } from '@/providers/location';
import { Link } from '@tanstack/react-router';
import { useContext } from 'react';
import { DashboardKpis } from '../components/DashboardKpis';
import { EventLookup } from '../components/EventLookup';
import { EventsToday } from '../components/EventsToday';
import { LeadLookup } from '../components/LeadLookup';
import { LeadsInbox } from '../components/LeadsInbox';
import { MessageOfTheDay } from '../components/MessageOfTheDay';
import { NewLeadForm } from '../components/NewLeadForm';
import { NewsFeed } from '../components/NewsFeed';

/** @Feature: Sales Stats */

export const Dashboard = () => {
  const { user } = useContext(AuthContext);

  const { activeLocationId: locationId } = useContext(LocationContext);
  const { checkRole } = useAuthorization();
  const { events: myEvents = [], depotAddress } = useShiftData(locationId, user?.user_id ?? 0);

  if (!user) return null;

  // Game Coaches have a unique dashboard experience focused on their daily shift activities, so we route them directly to that dashboard instead of showing the standard dashboard homepage.
  const isGameCoach = checkRole({ allowedRoles: ['Game Coach', 'Head Game Coach'] });
  // Other users might still have events assigned to them. If they do, we want to show them both the standard dashboard homepage and the shift dashboard, so they can choose which one to view. If they don't have any events assigned to them, we can skip showing the shift dashboard option since it would be empty and not relevant to their role.
  const hasEventsToday = myEvents.length > 0;

  // B2B sales reps get the B2B command center. Placed before the call-center
  // branch: some reps carry is_callcenter too, and B2B takes precedence.
  if (user.is_b2b) {
    return <B2bDashboard />;
  }

  // Call-center agents get the push-based guided queue instead of the browsing dashboard.
  // is_callcenter is a user-level job flag, so it deliberately outranks the
  // per-location Game Coach role branch below.
  if (user.is_callcenter && !user.is_b2b) {
    return <CallCenterDashboard />;
  }

  if (isGameCoach) {
    return <ShiftDashboard />;
  }

  return (
    <ScrollableLayout>
      <Page>
        <Page.Header className="mx-4">
          <Page.HeaderContent title={`Hi, ${user?.first_name}!`} />
          <Page.HeaderAction className="items-center">
            {/* Team View left the main nav deliberately -- non-agent managers
                reach the call-center viewer from here. */}
            <PolicyGate policy="callcenter.manager">
              <Button asChild variant="outline">
                <Link to="/callcenter/team">Call Center Team View</Link>
              </Button>
            </PolicyGate>
          </Page.HeaderAction>
        </Page.Header>
        <Page.Body policy="dashboard.view" className="@container space-y-4 p-4 py-2">
          {hasEventsToday && <ShiftEventSection events={myEvents} depotAddress={depotAddress} />}
          <PolicyGate policy="dashboard.motd">
            <MessageOfTheDay />
          </PolicyGate>
          <div className="grid grid-cols-1 @4xl:grid-cols-2 gap-2">
            <PolicyGate policy="leads.create">
              <NewLeadForm />
            </PolicyGate>
            <div className="grid grid-cols-1 @lg:grid-cols-2 gap-2">
              <PolicyGate policy="leads.view">
                <LeadLookup />
              </PolicyGate>
              <PolicyGate policy="events.view">
                <EventLookup />
              </PolicyGate>
            </div>
          </div>
          <PolicyGate policy="reports.view">
            <DashboardKpis />
          </PolicyGate>
          <div className="grid grid-cols-1 @2xl:grid-cols-3 gap-2">
            <PolicyGate policy="leads.view">
              <LeadsInbox />
            </PolicyGate>
            <PolicyGate policy="events.view">
              <EventsToday />
            </PolicyGate>
            <PolicyGate policy="dashboard.news">
              <NewsFeed />
            </PolicyGate>
          </div>
        </Page.Body>
      </Page>
    </ScrollableLayout>
  );
};
